import { Dialog, Transition } from '@headlessui/react'
import classNames from 'classnames'
import { isFunction } from 'lodash'
import { Fragment, useEffect, useRef } from 'react'
import useModalBackButton from '../../../modules/hooks/common/useModalBackButton'
import usePopupStore from '../../../store/usePopupStore'

export interface SidePopupPayload {
  id: string
  body: any
  title?: any
  onClose?: any
  overlayClose?: boolean
  noTopSide?: boolean //and X button disappear too.
  hFull?: boolean
  bgWhite?: boolean
  border?: boolean
  maxW?: number
  bgColor?: string
  noScrollbar?: boolean
  gapFromTop?: number
}

export const useSidePopup = () => {
  const popupShow = usePopupStore((state) => state.show)
  const popupClose = usePopupStore((state) => state.close)

  const show = (props: SidePopupPayload) => {
    popupShow({ ...props, type: 'side' })
  }
  const close = (id) => {
    popupClose({ id })
  }
  return { show, close }
}

export const SidePopup = ({ id }: { id: any }) => {
  const focusRef = useRef(null)

  const popupClose = usePopupStore((state) => state.close)
  const allPopups = usePopupStore((state) => state.data)
  const { data, isShow } = usePopupStore((state) => ({
    data: state.data[id],
    isShow: state.isPopupRendered,
  }))

  const open = data ? true : false
  const hasOtherPopupsOpen = Object.keys(allPopups).some((popupId) => popupId !== id && allPopups[popupId])

  const {
    title = '',
    body = undefined,
    onClose = null,
    maxW = 375,
    overlayClose = true,
    noTopSide = false,
    hFull = false,
    bgWhite = false,
    border = false,
    bgColor = 'white',
    noScrollbar = true,
    gapFromTop = 70,
  } = data ? data : {}

  const closeHandler = () => {
    popupClose({ id })
    if (isFunction(onClose)) {
      onClose()
    }
  }

  const onCloseOverlay = () => {
    if (!hasOtherPopupsOpen && overlayClose) {
      closeHandler()
    }
  }

  useModalBackButton(id, isShow)

  useEffect(() => {
    return () => {
      popupClose({ id })
    }
  }, [id])

  return (
    <div id={id}>
      <Transition appear show={open} as={Fragment}>
        <Dialog
          as="div"
          static
          className="no-scrollbar fixed inset-0 z-[2000] flex items-end justify-end overflow-y-auto overflow-x-hidden"
          initialFocus={focusRef}
          open={open}
          onClose={onCloseOverlay}
        >
          <button ref={focusRef} className="hidden" />
          <div className="flex-1">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100">
              <Dialog.Overlay
                className={classNames('fixed inset-0 transition-opacity', {
                  'bg-white-overlay': bgWhite,
                  'bg-dim': !bgWhite,
                })}
              />
            </Transition.Child>
            <span aria-hidden="true" className="hidden sm:align-middle">
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-x-20 sm:translate-y-0"
              enterTo="opacity-100 translate-x-0"
            >
              <div className="ml-auto transform overflow-hidden shadow-lg transition-all" style={{ maxWidth: maxW }}>
                <div
                  style={{ height: hFull ? `calc(100vh - ${gapFromTop}px)` : '80vh' }}
                  className={classNames(
                    { 'border-1': border },
                    { 'no-scrollbar': noScrollbar },
                    'flex flex-col overflow-y-auto bg-white shadow-lg'
                  )}
                >
                  {!noTopSide && (
                    <div className="relative m-[0px] flex rounded-t-[8px] px-[20px] py-[16px]">
                      <div className="w-full">
                        <Dialog.Title as="div" className="whitespace-normal pr-20" style={{ wordBreak: 'keep-all' }}>
                          {title}
                        </Dialog.Title>
                      </div>
                      {/* CANCEL BUTTON */}
                      <div className="absolute top-[34px] right-[32px] flex h-[20px] w-[20px] items-center justify-center">
                        <button
                          type="button"
                          className="appearance-none border-none focus:outline-none"
                          style={{ width: 20, height: 20 }}
                          onClick={closeHandler}
                        >
                          <i className="fe fe-x"></i>
                        </button>
                      </div>
                    </div>
                  )}
                  {/* BODY */}
                  <div style={{ backgroundColor: bgColor || 'white', height: '100%' }}>{body}</div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}
